import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box, IconButton, Typography, TextField,
  CircularProgress, Avatar,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import API_URL from '../config';
import actsLogo from '../assets/acts_logo.png';

const AI_BASE = `${API_URL}/ai-workspace`;

// Email read-only consent is persisted on the [user] table via the local API.
// Endpoints: GET/POST {API_URL}/email-access-consent
async function fetchEmailConsent() {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'GET',
    url: `${API_URL}/email-access-consent`,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000,
  });
  // null = undecided, true = granted, false = declined
  return r.data && r.data.email_access_consent;
}

async function saveEmailConsent(value) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'POST',
    url: `${API_URL}/email-access-consent`,
    data: { email_access_consent: value },
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    timeout: 30000,
  });
  return r.data && r.data.email_access_consent;
}

const PM = {
  primary: '#1e3c72',
  primaryDark: '#152b53',
  gradient: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
  bg: '#f4f6f8',
  border: '#e2e8f0',
  muted: '#64748b',
  faint: '#94a3b8',
  dark: '#1e293b',
};

function parseChatResponse(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  let text = '';
  if (typeof data.answer === 'string') text = data.answer;
  else if (typeof data.response === 'string') text = data.response;
  else if (typeof data.reply === 'string') text = data.reply;
  else if (typeof data.message === 'string') text = data.message;
  else if (typeof data.result === 'string') text = data.result;
  else if (typeof data.content === 'string') text = data.content;
  else if (typeof data.answer === 'object' && data.answer && typeof data.answer.text === 'string') text = data.answer.text;
  else if (typeof data.output === 'string') text = data.output;
  else if (Array.isArray(data.messages) && data.messages.length) {
    const last = [...data.messages].reverse().find((m) => m && (m.role === 'assistant' || m.role === 'ai') && typeof m.content === 'string');
    if (last) text = last.content;
  }
  return text;
}

function isTableSeparator(line) {
  return /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');
}

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function parseBlocks(text) {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'blockquote', content: quoteLines.join('\n') });
      continue;
    }
    const textLines = [];
    while (i < lines.length) {
      const l = lines[i];
      const nextIsTable = l.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]);
      const nextIsQuote = /^\s*>/.test(l);
      if (nextIsTable || nextIsQuote) break;
      textLines.push(l);
      i++;
    }
    if (textLines.some((l) => l.trim() !== '')) {
      blocks.push({ type: 'text', content: textLines.join('\n') });
    } else {
      i++;
    }
  }
  return blocks;
}

function MessageContent({ content, isUser }) {
  const blocks = parseBlocks(content || '');
  const textColor = isUser ? '#fff' : PM.dark;
  return (
    <Box>
      {blocks.map((b, idx) => {
        if (b.type === 'table') {
          return (
            <TableContainer key={idx} component={Paper} variant="outlined"
              sx={{ my: 1, maxWidth: '100%', overflowX: 'auto', borderRadius: 2 }}>
              <Table size="small" sx={{ minWidth: 200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(30,60,114,0.06)' }}>
                    {(b.header || []).map((h, hi) => (
                      <TableCell key={hi} sx={{ fontWeight: 800, color: PM.dark, whiteSpace: 'nowrap' }}>
                        {renderTextWithLinks(h, isUser)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {b.rows.map((r, ri) => (
                    <TableRow key={ri}>
                      {(b.header || []).map((_, ci) => (
                        <TableCell key={ci} sx={{ color: PM.dark, whiteSpace: 'nowrap' }}>
                          {renderTextWithLinks(r[ci] ?? '', isUser)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }
        if (b.type === 'blockquote') {
          return (
            <Box key={idx} sx={{
              my: 1, pl: 1.5, py: 0.75, pr: 1,
              borderLeft: '4px solid #cbd5e1',
              bgcolor: '#f8fafc',
              borderRadius: '0 8px 8px 0',
            }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: PM.muted, fontStyle: 'italic' }}>
                {renderTextWithLinks(b.content, isUser)}
              </Typography>
            </Box>
          );
        }
        return (
          <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColor }}>
            {renderTextWithLinks(b.content, isUser)}
          </Typography>
        );
      })}
    </Box>
  );
}

function renderTextWithLinks(text, isUser) {
  if (!text) return text;
  const urlRe = /(https?:\/\/[^\s<>"')\]]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;
  const parts = String(text).split(urlRe);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^(https?:\/\/)/i.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
          style={{ color: isUser ? '#bcd3ff' : '#1e40af', textDecoration: 'underline', cursor: 'pointer' }}>
          Open link
        </a>
      );
    }
    if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(part)) {
      return (
        <a key={i} href={`mailto:${part}`}
          style={{ color: isUser ? '#bcd3ff' : '#1e40af', textDecoration: 'underline', cursor: 'pointer' }}>
          Open link
        </a>
      );
    }
    return <span key={i}>{renderInline(part, isUser)}</span>;
  });
}

function renderInline(text, isUser) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={i} style={{ color: isUser ? '#fff' : PM.dark }}>{part.slice(2, -2)}</strong>;
    }
    if (/^\*[^*]+\*$/.test(part)) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{part}</span>;
  });
}

async function request(userQuery, threadId) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'POST',
    url: `${AI_BASE}/chat`,
    data: {
      user_query: userQuery,
      thread_id: threadId,
    },
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    timeout: 600000,
  });
  return r.data;
}

async function fetchThreads() {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'GET',
    url: `${AI_BASE}/threads`,
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return r.data;
}

async function fetchThreadHistory(threadId) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'GET',
    url: `${AI_BASE}/thread_history`,
    params: { thread_id: threadId },
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return r.data;
}

async function deleteThread(threadId) {
  const token = localStorage.getItem('token');
  const r = await axios({
    method: 'DELETE',
    url: `${AI_BASE}/threads`,
    params: { thread_id: threadId },
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return r.data;
}

async function uploadFiles(threadId, fileList) {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('thread_id', threadId);
  for (const file of fileList) {
    formData.append('files', file);
  }
  const r = await axios({
    method: 'POST',
    url: `${AI_BASE}/upload_files`,
    data: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    timeout: 600000,
  });
  return r.data;
}

function ACTSAssistant() {
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [threads, setThreads] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentLoading, setConsentLoading] = useState(true);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentScrolled, setConsentScrolled] = useState(false);
  const fileInputRef = useRef(null);

  // Marks the legal text as fully read once the user scrolls to the bottom.
  const handleConsentScroll = (e) => {
    if (consentScrolled) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 8) {
      setConsentScrolled(true);
    }
  };
  const bottom = useRef(null);

  useEffect(() => {
    // On first open, load the saved consent from the backend and, if the user
    // has never answered (null), open the prompt.
    (async () => {
      try {
        const saved = await fetchEmailConsent();
        if (saved === true) {
          setEmailConsent(true);
        } else if (saved === false) {
          setEmailConsent(false);
        } else {
          // null -> not yet decided, prompt on first open.
          setEmailConsent(false);
          setConsentOpen(true);
        }
      } catch {
        // Could not load consent; keep prompt closed and let the header button
        // allow the user to open it manually.
        setEmailConsent(false);
      } finally {
        setConsentLoading(false);
      }
    })();
  }, []);

  const grantEmailConsent = async () => {
    setConsentSaving(true);
    try {
      await saveEmailConsent(true);
      setEmailConsent(true);
      setConsentOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not save email consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  const declineEmailConsent = async () => {
    setConsentSaving(true);
    try {
      await saveEmailConsent(false);
      setEmailConsent(false);
      setConsentOpen(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not save email consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  const revokeEmailConsent = async () => {
    setConsentSaving(true);
    try {
      await saveEmailConsent(false);
      setEmailConsent(false);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not revoke email consent.');
    } finally {
      setConsentSaving(false);
    }
  };

  useEffect(() => {
    setError('');
    if (!threadId) setThreadId(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
  }, [threadId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, busy]);

  const newChat = () => {
    setMessages([]);
    setThreadId(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
    setPrompt('');
    setAttachments([]);
  };

  const handleAttach = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAttachments((prev) => [...prev, ...files]);
    // Reset the input so selecting the same file again re-triggers onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const loadThreads = async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const list = await fetchThreads();
      setThreads(Array.isArray(list) ? list : []);
    } catch (err) {
      setThreads([]);
      setHistoryError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Could not load history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadThreads();
  }, []);

  const requestDelete = (e, t) => {
    e.stopPropagation();
    setDeleteTarget(t);
  };

  const confirmDelete = async () => {
    const t = deleteTarget;
    if (!t) return;
    const id = t?.thread_id || t?.threadId || t?.id || t?.session_id;
    setDeleteTarget(null);
    if (!id) return;
    try {
      await deleteThread(id);
      setThreads((prev) => prev.filter((x) => (x?.thread_id || x?.threadId || x?.id || x?.session_id) !== id));
      if (threadId === id) {
        setMessages([]);
        setThreadId(crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
      }
    } catch (err) {
      setHistoryError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Could not delete conversation.');
    }
  };

  const selectThread = async (t) => {
    const id = t?.thread_id || t?.threadId || t?.id || t?.session_id;
    if (!id) return;
    setBusy(true);
    setError('');
    try {
      const result = await fetchThreadHistory(id);
      const msgs = Array.isArray(result?.messages) ? result.messages
        : (Array.isArray(result) ? result : []);
      const nextId = result?.thread_id || id;
      setThreadId(nextId);
      setMessages(msgs.map((m) => ({ role: m.role || 'user', content: m.content || '' })));
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Could not load conversation.');
      setThreadId(id);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = prompt.trim();
    if ((!text && attachments.length === 0) || busy) return;
    setError('');
    setBusy(true);

    let pendingAttachments = attachments;
    setAttachments([]);
    setPrompt('');

    // Show the user's message (and any attached file names) immediately.
    const attachSummary = pendingAttachments.length
      ? '\n📎 ' + pendingAttachments.map((f) => f.name).join(', ')
      : '';
    setMessages((prev) => [...prev, { role: 'user', content: (text || '(attachments)') + attachSummary }]);

    try {
      // Upload any attached files to the ACTS AI workspace first.
      if (pendingAttachments.length) {
        setUploading(true);
        await uploadFiles(threadId, pendingAttachments);
        setUploading(false);
      }

      // Only call /chat when there is actual text; attachments alone just upload.
      if (text) {
        const result = await request(text, threadId);
        const answer = parseChatResponse(result);
        setMessages((prev) => [...prev, { role: 'assistant', content: answer || 'No answer returned.' }]);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Request failed.');
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };



  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box', height: 'calc(100vh - 4rem)', overflow: 'hidden', bgcolor: PM.bg, display: 'flex', flexDirection: 'column', fontFamily: '"Nunito", "Segoe UI", sans-serif' }}>
      <Box sx={{
        pt: { xs: 4, md: 5 }, pb: { xs: 8, md: 9 }, px: { xs: 3, md: 5 },
        background: PM.gradient,
        borderRadius: '0 0 40px 40px', mb: -5,
        boxShadow: '0 15px 35px rgba(30, 60, 114, 0.2)',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: '16px', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.3)' }} variant="rounded">
          <img src={actsLogo} alt="ACTS" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Avatar>
        <Box flex={1}>
          <Typography variant="h4" fontWeight="900" color="#fff" lineHeight={1.2} sx={{ mb: 0.5, textShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            ACTS Assistant
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.85)" fontWeight={600}>
            Private AI workspace
          </Typography>
        </Box>
        <Button
          variant="contained" size="small" startIcon={consentLoading ? <CircularProgress size={16} color="inherit" /> : (emailConsent ? <MarkEmailReadIcon /> : <MailOutlineIcon />)}
          onClick={() => { if (emailConsent) { revokeEmailConsent(); } else { setConsentChecked(false); setConsentScrolled(false); setConsentOpen(true); } }}
          disabled={consentLoading || consentSaving}
          title={emailConsent ? 'AI can read your inbox (read-only). Click to revoke.' : 'Allow the AI to read your inbox (read-only).'}
          sx={{ bgcolor: emailConsent ? 'rgba(74,222,128,0.25)' : 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, borderRadius: '50px', px: 2, py: 1, textTransform: 'none', backdropFilter: 'blur(8px)', '&:hover': { bgcolor: emailConsent ? 'rgba(74,222,128,0.38)' : 'rgba(255,255,255,0.3)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)' } }}
        >
          {emailConsent ? 'Email access on' : 'Allow email access'}
        </Button>
        <Button
          variant="contained" size="small" startIcon={<AddIcon />}
          onClick={newChat}
          sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 800, borderRadius: '50px', px: 2.5, py: 1, backdropFilter: 'blur(8px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' } }}
        >
          New chat
        </Button>
      </Box>

      {error && (
        <Box mx={{ xs: 2, md: 4 }} mt={2} px={2} py={1.25} borderRadius={2} sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontSize: 13 }}>{error}</Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', p: { xs: 2, md: 4 }, gap: 2.5, position: 'relative', zIndex: 2 }}>

        {/* Left: conversation history panel */}
        <Paper elevation={0} sx={{
          width: 280, flexShrink: 0, minHeight: 0, borderRadius: 3, border: '1px solid', borderColor: PM.border,
          bgcolor: '#fff',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: PM.border }}>
            <Typography variant="subtitle2" fontWeight={800} color={PM.primary} letterSpacing={0.4} sx={{ mb: 1 }}>
              History
            </Typography>
            <Button
              fullWidth variant="contained" size="small" startIcon={<AddIcon />}
              onClick={newChat}
              sx={{ borderRadius: 2, fontWeight: 700, bgcolor: PM.primary, '&:hover': { bgcolor: PM.primaryDark } }}
            >
              New conversation
            </Button>
          </Box>
          <Box flex={1} overflow="auto" py={1}>
            {historyLoading && (
              <Box px={2} py={2} display="flex" alignItems="center" gap={1}>
                <CircularProgress size={15} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            )}
            {!historyLoading && historyError && (
              <Box px={2} py={2}>
                <Typography variant="body2" color="#b91c1c">{historyError}</Typography>
              </Box>
            )}
            {!historyLoading && !historyError && threads.length === 0 && (
              <Box px={2} py={2}>
                <Typography variant="body2" color="text.secondary">No conversations yet.</Typography>
              </Box>
            )}
            {!historyLoading && threads.map((t, i) => {
              const tid = t?.thread_id || t?.threadId || t?.id || t?.session_id;
              const active = tid === threadId;
              return (
                <Box key={tid || i}
                  onClick={() => selectThread(t)}
                  sx={{
                    mx: 1.5, mb: 0.5, px: 1.5, py: 1.25, borderRadius: 2.5, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    bgcolor: active ? 'rgba(30,60,114,0.08)' : 'transparent',
                    borderLeft: '3px solid', borderLeftColor: active ? PM.primary : 'transparent',
                    '&:hover': { bgcolor: 'rgba(30,60,114,0.05)' },
                  }}>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" noWrap fontWeight={active ? 700 : 500} color={active ? PM.primary : PM.dark}>
                      {t?.title || t?.name || `Conversation ${i + 1}`}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => requestDelete(e, t)} sx={{ color: PM.faint, '&:hover': { color: '#b91c1c' } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* Right: chat panel */}
        <Paper elevation={0} sx={{ flex: 1, minHeight: 0, borderRadius: 3, border: '1px solid', borderColor: PM.border, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box flex={1} overflow="auto" px={{ xs: 2, md: 4 }} py={3} sx={{ bgcolor: '#fafbfe' }}>

          {messages.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
              height="100%" textAlign="center" gap={1.5} color="text.secondary">
              <Box sx={{ width: 84, height: 84, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(30,60,114,0.08)', p: 1.5 }}>
                <img src={actsLogo} alt="ACTS" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Box>
              <Typography variant="h6" fontWeight={800} color={PM.dark}>
                Start a conversation
              </Typography>
              <Typography variant="body2" color={PM.muted} sx={{ maxWidth: 360 }}>
                Ask a question and let the assistant help you work smarter.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.75}>
              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{
                      maxWidth: '74%',
                      px: 2, py: 1.25,
                      borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      bgcolor: isUser ? PM.primary : '#f1f5f9',
                      color: isUser ? '#fff' : PM.dark,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}>
                      <Typography variant="caption" fontWeight={800}
                        color={isUser ? 'rgba(255,255,255,0.85)' : PM.primary}
                        sx={{ display: 'block', mb: 0.25 }}>
                        {isUser ? 'You' : 'ACTS Assistant'}
                      </Typography>
                      <Box sx={{ color: isUser ? '#fff' : PM.dark }}>
                        <MessageContent content={m.content} isUser={isUser} />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              {busy && (
                <Box display="flex" justifyContent="flex-start">
                  <Box sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: '#f1f5f9' }}>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <CircularProgress size={15} /> Thinking…
                    </Box>
                  </Box>
                </Box>
              )}
              <div ref={bottom} />
            </Box>
          )}
        </Box>


        <Box px={2.5} py={2} sx={{ borderTop: '1px solid', borderColor: PM.border, bgcolor: '#fff' }}>
          {attachments.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1} mb={1.25}>
              {attachments.map((f, i) => (
                <Box key={i} display="flex" alignItems="center" gap={0.75}
                  sx={{ px: 1.25, py: 0.5, borderRadius: 8, bgcolor: 'rgba(30,60,114,0.08)', border: '1px solid', borderColor: PM.border }}>
                  <AttachFileIcon sx={{ fontSize: 15, color: PM.primary }} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: 160, fontWeight: 700, color: PM.dark }}>
                    {f.name}
                  </Typography>
                  <IconButton size="small" onClick={() => removeAttachment(i)} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14, color: PM.muted }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Box display="flex" gap={1.25} alignItems="flex-end">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".doc,.docx,.pdf,.xls,.xlsx,.xlsm,.csv,.txt"
              style={{ display: 'none' }}
              onChange={handleAttach}
            />
            <IconButton onClick={() => fileInputRef.current?.click()} disabled={busy}
              aria-label="Attach a file"
              sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#eef2f7', color: PM.primary, '&:hover': { bgcolor: '#e2e8f0' }, '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#cbd5e1' } }}>
              <AttachFileIcon fontSize="small" />
            </IconButton>
            <TextField
              fullWidth multiline minRows={1} maxRows={4}
              variant="outlined" size="small"
              placeholder="Message the assistant…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 8, bgcolor: '#f8fafc', '& fieldset': { borderColor: PM.border } } }}
            />
            <IconButton onClick={send} disabled={(!prompt.trim() && attachments.length === 0) || busy}
              sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: PM.primary, color: '#fff', '&:hover': { bgcolor: PM.primaryDark }, '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' } }}>
              {busy ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Paper>
      </Box>

      <Dialog open={consentOpen} onClose={declineEmailConsent} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MarkEmailReadIcon sx={{ color: PM.primary }} />
          Allow email access
        </DialogTitle>
        <DialogContent>
          <Box
            onScroll={handleConsentScroll}
            sx={{
              maxHeight: 380, overflowY: 'auto',
              bgcolor: '#f8fafc', border: '1px solid', borderColor: PM.border,
              borderRadius: 2, px: 2.5, py: 2, mb: 1.5,
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Please review the following agreement before granting access.
            </Typography>
            <Typography variant="subtitle2" fontWeight={800} color={PM.primary} sx={{ mb: 1, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              Email Read-Only Access Agreement
            </Typography>
            <Typography variant="body2" color={PM.dark} sx={{ lineHeight: 1.75, whiteSpace: 'pre-line' }}>
              {`This Email Read-Only Access Agreement ("Agreement") is entered into by and between you ("User") and ACTS Assistant ("the Service"). By selecting the checkbox below and clicking "Allow read-only access", you expressly acknowledge that you have read, understood, and agreed to be bound by all of the terms and conditions set forth herein.

              1. Grant of Limited Access. Subject to the terms of this Agreement, you grant the Service a limited, revocable, non-exclusive, non-transferable, and non-sublicensable right to access and read the contents of your email inbox, solely for the purpose of providing you with personalised assistance, summarising correspondence, and drafting responses on your behalf. You retain all right, title, and interest in and to your email content.

              2. Read-Only Limitation. The access granted hereunder is strictly read-only. The Service is expressly prohibited from, and you do not authorise the Service to, transmit, forward, delete, modify, archive, or otherwise alter any email, message, attachment, contact, calendar item, or other data within your mailbox in any manner whatsoever.

              3. Data Use and Confidentiality. Your email content will be used exclusively to fulfil your direct requests and to improve the accuracy of responses provided to you. The Service will not use your email content to train any machine-learning model, will not disclose your content to any third party, and will not share, sell, rent, or otherwise commercialise your information, except as required by applicable law.

              4. Security and Retention. The Service employs reasonable technical and organisational measures to protect your email content against unauthorised access, disclosure, alteration, and destruction. Any transient copies of your content created during processing are retained only for as long as is reasonably necessary to provide the Service and are thereafter deleted.

              5. Your Responsibilities. You are solely responsible for ensuring that you have the lawful right and authority to grant the access described herein, and that such access does not violate any duty of confidentiality, contractual obligation, or third-party right to which you are subject.

              6. Revocation. You may revoke the access granted under this Agreement at any time and for any reason, without penalty, by selecting the revoke option available in the Service header. Upon revocation, the Service shall promptly cease accessing your inbox.

              7. Disclaimer and Limitation of Liability. The Service is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied. To the fullest extent permitted by law, the Service shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or in connection with this Agreement.

              8. Governing Law. This Agreement shall be governed by and construed in accordance with the laws of the applicable jurisdiction, without regard to its conflict-of-law provisions.

              By scrolling to the end of this document, marking the acknowledgement checkbox, and selecting "Allow read-only access", you confirm your acceptance of the terms described above and authorise the Service to access your email inbox on a read-only basis.`}
            </Typography>
          </Box>
          {!consentScrolled ? (
            <Typography variant="caption" color={PM.muted} sx={{ display: 'block', mb: 1, textAlign: 'center', fontStyle: 'italic' }}>
              Scroll to the end of the agreement to accept.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} color="primary" />}
                label={<Typography variant="body2" color={PM.dark}>I have read and agree to the terms of this Email Read-Only Access Agreement.</Typography>}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={declineEmailConsent} disabled={consentSaving} color="inherit">Not now</Button>
          <Button onClick={grantEmailConsent} disabled={consentSaving || !consentChecked || !consentScrolled} variant="contained" autoFocus sx={{ bgcolor: PM.primary, '&:hover': { bgcolor: PM.primaryDark } }}>
            {consentSaving ? <CircularProgress size={18} color="inherit" /> : 'Allow read-only access'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete conversation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete
            {' '}“{deleteTarget?.title || deleteTarget?.name || 'this conversation'}”.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ACTSAssistant;

